/**
 * CostByModelChart - Vertical bar chart showing top models by selected metric
 *
 * Features:
 * - Aggregates cost/token data by model across all timestamps
 * - Shows top 10 models sorted by selected metric
 * - Vertical bars (models on X-axis, values on Y-axis)
 * - Legend at bottom (matching CostOverTimeChart design)
 * - Uses same color palette as CostOverTimeChart
 */

import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useNavigate } from 'react-router-dom';
import { palette, typography } from '../../utils/designTokens';
import { getChartColors } from '../../utils/chartColors';
import { serializeFilters } from '../../utils/urlBuilder';

/**
 * Process cost data to aggregate by model
 */
const processDataByModel = (costOverTimeData, selectedMetric) => {
  if (!costOverTimeData || costOverTimeData.length === 0) {
    return { chartData: [], modelList: [], chartColors: {} };
  }

  // Aggregate totals by model
  const modelTotals = {};

  costOverTimeData.forEach(({ models }) => {
    models.forEach((model) => {
      const modelKey = model.model_name;

      if (!modelTotals[modelKey]) {
        modelTotals[modelKey] = {
          model_name: model.model_name,
          provider: model.provider,
          cost_usd: 0,
          total_tokens: 0,
          prompt_tokens: 0,
          completion_tokens: 0,
          cached_tokens: 0
        };
      }

      modelTotals[modelKey].cost_usd += model.cost_usd || 0;
      modelTotals[modelKey].total_tokens += model.total_tokens || 0;
      modelTotals[modelKey].prompt_tokens += model.prompt_tokens || 0;
      modelTotals[modelKey].completion_tokens += model.completion_tokens || 0;
      modelTotals[modelKey].cached_tokens += model.cached_tokens || 0;
    });
  });

  // Convert to array and sort by selected metric
  let modelArray = Object.values(modelTotals);

  // Sort by selected metric (descending)
  modelArray.sort((a, b) => {
    const aValue = a[selectedMetric] || 0;
    const bValue = b[selectedMetric] || 0;
    return bValue - aValue;
  });

  // Take top 10
  modelArray = modelArray.slice(0, 10);

  // Get model names list
  const modelList = modelArray.map(d => d.model_name);

  // Generate model colors
  const chartColors = getChartColors(modelList);

  // Transform data for vertical bar chart
  // Create one data point with all models as separate keys
  const chartData = [{
    category: 'Models'  // Single category for all models
  }];

  modelArray.forEach(model => {
    chartData[0][model.model_name] = model[selectedMetric] || 0;
    // Store metadata for tooltip
    chartData[0][`${model.model_name}_provider`] = model.provider;
    chartData[0][`${model.model_name}_data`] = model;
  });

  return { chartData, modelList, chartColors };
};

const CostByModelChart = ({ data, selectedMetric, timeWindow, appId, environment }) => {
  const navigate = useNavigate();
  const [highlightedModel, setHighlightedModel] = React.useState(null);

  // Process data
  const { chartData, modelList, chartColors } = useMemo(
    () => processDataByModel(data, selectedMetric),
    [data, selectedMetric]
  );

  // Handle bar click for drilldown
  const handleBarClick = (modelName) => {
    if (!modelName) return;

    // Map selectedMetric to sort parameter
    const sortMapping = {
      'cost_usd': 'cost_desc',
      'total_tokens': 'total_tokens_desc',
      'prompt_tokens': 'prompt_tokens_desc',
      'completion_tokens': 'completion_tokens_desc',
      'cached_tokens': 'cached_tokens_desc'
    };
    const sortParam = sortMapping[selectedMetric] || 'cost_desc';

    // Navigate to Traces page filtered by this model
    const filters = {
      model: modelName,
      sort: sortParam,
      env: environment,
      range: timeWindow,
      from: 'cost'  // Track that user came from Cost tab for breadcrumb
    };
    const url = `/app/${appId}/traces?${serializeFilters(filters)}`;
    navigate(url);
  };

  const hasData = modelList.length > 0;

  const containerRef = React.useRef(null);

  if (!hasData) {
    return (
      <div
        style={{
          height: 300,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: palette.text.muted,
          fontSize: '14px'
        }}
      >
        No data available for the selected time window
      </div>
    );
  }

  // Format Y-axis labels (values) - matching CostOverTimeChart exactly
  const formatYAxis = (value) => {
    if (selectedMetric === 'cost_usd') {
      // Format as dollars
      if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
      if (value >= 1) return `$${value.toFixed(0)}`;
      if (value >= 0.01) return `$${value.toFixed(2)}`;
      if (value > 0) return `$${value.toFixed(4)}`;
      return '$0';
    } else {
      // Format as tokens (K or M)
      if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
      if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
      return value.toFixed(0);
    }
  };

  return (
    <ResponsiveContainer width="100%" height={280} ref={containerRef}>
        <BarChart
          data={chartData}
          margin={{ top: 10, right: 20, left: 20, bottom: 5 }}
          barCategoryGap="10%"
        >
          <CartesianGrid strokeDasharray="3 3" stroke={palette.support.gridline} opacity={0.5} />
          <XAxis
            dataKey="category"
            tick={{ fontSize: 10, fill: palette.text.muted }}
            stroke={palette.neutrals.borderSubtle}
            hide={true}  // Hide X-axis since we show models in legend
          />
          <YAxis
            tickFormatter={formatYAxis}
            tick={{ fontSize: 10, fill: palette.text.muted }}
            stroke={palette.neutrals.borderSubtle}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: palette.neutrals.panelQuaternary,
              border: `1px solid ${palette.neutrals.borderStrong}`,
              borderRadius: '4px',
              fontSize: '12px',
              padding: '8px'
            }}
            labelStyle={{ color: palette.text.heading, fontWeight: 600 }}
            itemStyle={{ color: palette.text.primary }}
            formatter={(value, name) => {
              // Format the value with proper units
              let displayValue;
              if (selectedMetric === 'cost_usd') {
                displayValue = `$${value.toFixed(4)}`;
              } else {
                displayValue = value.toLocaleString();
              }

              return [displayValue, name];
            }}
            cursor={false}
          />
          <Legend
            wrapperStyle={{ fontSize: '10px', paddingTop: '5px' }}
            iconType="square"
          />

          {/* One Bar per model */}
          {modelList.map((modelName) => (
            <Bar
              key={modelName}
              dataKey={modelName}
              fill={chartColors[modelName]}
              fillOpacity={highlightedModel === modelName ? 0.9 : 0.7}
              onClick={() => handleBarClick(modelName)}
              onMouseEnter={() => setHighlightedModel(modelName)}
              onMouseLeave={() => setHighlightedModel(null)}
              style={{ cursor: 'pointer' }}
              radius={[4, 4, 0, 0]}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
  );
};

export default CostByModelChart;
